import React, { useState } from 'react';
import { ExportBlock } from '../../types';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { downloadChatExport } from '../../api';
import { toast } from 'sonner';

interface ExportBlockRendererProps {
  block: ExportBlock;
}

export const ExportBlockRenderer: React.FC<ExportBlockRendererProps> = ({ block }) => {
  const { label, url } = block;
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const { blob, filename } = await downloadChatExport(url);

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Downloaded ${filename}`);
    } catch (err: any) {
      console.error('Export download error:', err);
      const errMsg = err?.message || 'Failed to download export';
      setDownloadError(errMsg);
      toast.error(errMsg);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="my-3 inline-block">
      <div className="flex flex-col gap-1.5 items-start">
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:shadow"
        >
          {isDownloading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <ArrowDownTrayIcon className="w-4 h-4" />
          )}
          <span>{label || 'Download Excel'}</span>
        </button>

        {downloadError && (
          <span className="text-xs text-rose-600 font-medium">⚠️ {downloadError}</span>
        )}
      </div>
    </div>
  );
};
